import React, { useState, useEffect, useRef } from 'react';
import { TextField, Autocomplete, CircularProgress, InputAdornment } from '@mui/material';
import { LocationOn } from '@mui/icons-material';
import adresseService, { AdresseFeature } from '../services/adresseService';

// Fonction debounce personnalisée (évite d'importer lodash)
function debounce<T extends (...args: any[]) => any>(func: T, wait: number) {
  let timeout: NodeJS.Timeout | null = null;
  const debounced = (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
  debounced.cancel = () => {
    if (timeout) clearTimeout(timeout);
  };
  return debounced;
}

interface AddressAutocompleteProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  onAddressSelect?: (address: {
    adresse: string;
    codePostal: string;
    ville: string;
  }) => void;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * Composant d'autocomplétion d'adresses françaises
 * Utilise l'API Adresse du gouvernement français
 */
export const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  label = 'Adresse',
  value,
  onChange,
  onAddressSelect,
  error = false,
  helperText,
  required = false,
  placeholder = 'Ex: 8 Boulevard du Port, Amiens',
  disabled = false,
}) => {
  const [options, setOptions] = useState<AdresseFeature[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');

  // Fonction de recherche
  const searchAddress = async (searchText: string) => {
    if (searchText.length < 3) {
      setOptions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const results = await adresseService.searchAdresse(searchText, 8);
      setOptions(results);
    } catch (error) {
      console.error('Erreur autocomplétion adresse:', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  // Fonction de recherche débounced (attend 300ms après la dernière frappe)
  const debouncedSearchRef = useRef(debounce(searchAddress, 300));
  const debouncedSearch = debouncedSearchRef.current;

  // Effectuer la recherche quand l'input change
  useEffect(() => {
    if (inputValue && inputValue.length >= 3) {
      debouncedSearch(inputValue);
    } else {
      setOptions([]);
    }

    // Cleanup
    return () => {
      debouncedSearch.cancel();
    };
  }, [inputValue, debouncedSearch]);

  // Synchroniser avec la valeur externe
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const handleSelect = (event: any, newValue: AdresseFeature | string | null) => {
    if (typeof newValue === 'string') {
      // L'utilisateur a saisi une adresse manuellement
      onChange(newValue);
      setInputValue(newValue);
    } else if (newValue && typeof newValue === 'object') {
      // L'utilisateur a sélectionné une suggestion
      const formatted = adresseService.formatAdresseForForm(newValue);
      onChange(formatted.adresse);
      setInputValue(formatted.adresse);

      // Notifier le parent avec tous les champs
      if (onAddressSelect) {
        onAddressSelect(formatted);
      }
    } else {
      // Réinitialisation
      onChange('');
      setInputValue('');
    }
  };

  const handleInputChange = (event: any, newInputValue: string) => {
    setInputValue(newInputValue);
    onChange(newInputValue);
  };

  return (
    <Autocomplete
      freeSolo
      options={options}
      loading={loading}
      disabled={disabled}
      value={null} // Toujours null pour permettre la saisie libre
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleSelect}
      getOptionLabel={(option) => {
        if (typeof option === 'string') {
          return option;
        }
        return option.properties.label;
      }}
      filterOptions={(x) => x} // Désactiver le filtre local (déjà fait par l'API)
      noOptionsText={
        inputValue.length < 3
          ? 'Saisissez au moins 3 caractères'
          : loading
          ? 'Recherche en cours...'
          : 'Aucune adresse trouvée'
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error}
          helperText={helperText}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <LocationOn color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...otherProps } = props as any;
        return (
          <li key={option.properties.id} {...otherProps}>
            <LocationOn sx={{ mr: 1, color: 'text.secondary' }} />
            <div>
              <div style={{ fontWeight: 500 }}>{option.properties.label}</div>
              <div style={{ fontSize: '0.85em', color: 'text.secondary' }}>
                {option.properties.context}
              </div>
            </div>
          </li>
        );
      }}
    />
  );
};

export default AddressAutocomplete;
